import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface CreditTransaction {
  id: string;
  createdAt: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  status: string;
  merchantName?: string;
}

interface ManagerCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ManagerCreditModal: React.FC<ManagerCreditModalProps> = ({ isOpen, onClose }) => {
  const [credits, setCredits] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchCreditTransactions(currentPage);
    }
  }, [isOpen, currentPage]);

  const fetchCreditTransactions = async (page: number) => {
    try {
      setLoading(true);
      
      // Fetch all credit transactions (manager can see all)
      const response = await api.get(`/dashboard/admin/stats`);
      const statsData = response.data.data;
      
      // For now, we'll use mock data since we don't have a specific credit transactions endpoint
      // In a real implementation, you'd have an endpoint like /dashboard/manager/credit-transactions
      const mockCredits = [
        {
          id: '1',
          createdAt: new Date().toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }),
          type: 'System Credit',
          amount: 50000,
          balanceAfter: 50000,
          description: 'Initial system credit allocation',
          status: 'Completed',
          merchantName: 'System'
        },
        {
          id: '2',
          createdAt: new Date(Date.now() - 86400000).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }),
          type: 'Merchant Credit',
          amount: 25000,
          balanceAfter: 75000,
          description: 'Credit to merchant wallet',
          status: 'Completed',
          merchantName: 'Demo Merchant'
        }
      ];

      setCredits(mockCredits);
      setTotalCreditAmount(75000);
      setTotalCredits(2);
      setTotalPages(1);
    } catch (error) {
      console.error('Error fetching manager credit transactions:', error);
      setCredits([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center py-4 px-5 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
          <h2 className="text-lg font-bold text-slate-800 m-0">Manager Credit Overview</h2>
          <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 cursor-pointer hover:bg-slate-200 text-2xl" onClick={onClose}>&times;</button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
            </div>
          ) : credits.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <div className="text-5xl mb-4">📭</div>
              <h3 className="text-xl font-bold text-slate-800 mb-2 m-0">No Credit Transactions Found</h3>
              <p className="text-slate-600 m-0">No credit transactions have been recorded yet.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-violet-50 border border-violet-200">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl bg-violet-500 text-white">💰</div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 m-0 mb-1">Total Credit Amount</h4>
                    <p className="text-xl font-bold text-slate-800 m-0">{formatCurrency(totalCreditAmount)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-pink-50 border border-pink-200">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl bg-pink-500 text-white">⬆️</div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 m-0 mb-1">Total Credits</h4>
                    <p className="text-xl font-bold text-slate-800 m-0">{totalCredits}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50 border border-blue-200">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl bg-blue-500 text-white">🏪</div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 m-0 mb-1">Affected Merchants</h4>
                    <p className="text-xl font-bold text-slate-800 m-0">{new Set(credits.map(c => c.merchantName)).size}</p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-base font-bold text-slate-800 mb-3 m-0">Recent Credit Transactions</h3>
                <div className="bg-white rounded-xl overflow-x-auto border border-slate-200">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gradient-to-br from-slate-50 to-slate-100">
                        <th className="text-slate-700 font-bold py-3 px-4 text-left border-b-2 border-slate-200 text-xs uppercase tracking-wider">Date</th>
                        <th className="text-slate-700 font-bold py-3 px-4 text-left border-b-2 border-slate-200 text-xs uppercase tracking-wider">Type</th>
                        <th className="text-slate-700 font-bold py-3 px-4 text-left border-b-2 border-slate-200 text-xs uppercase tracking-wider">Amount</th>
                        <th className="text-slate-700 font-bold py-3 px-4 text-left border-b-2 border-slate-200 text-xs uppercase tracking-wider">Merchant</th>
                        <th className="text-slate-700 font-bold py-3 px-4 text-left border-b-2 border-slate-200 text-xs uppercase tracking-wider">Description</th>
                        <th className="text-slate-700 font-bold py-3 px-4 text-left border-b-2 border-slate-200 text-xs uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credits.map(credit => (
                        <tr key={credit.id} className="hover:bg-slate-50">
                          <td className="py-3 px-4 border-b border-slate-100 text-slate-700 align-middle text-xs">{credit.createdAt}</td>
                          <td className="py-3 px-4 border-b border-slate-100 text-slate-700 align-middle">{credit.type}</td>
                          <td className="py-3 px-4 border-b border-slate-100 text-slate-700 align-middle font-semibold">{formatCurrency(credit.amount)}</td>
                          <td className="py-3 px-4 border-b border-slate-100 text-slate-700 align-middle">{credit.merchantName}</td>
                          <td className="py-3 px-4 border-b border-slate-100 text-slate-700 align-middle max-w-[200px] truncate">{credit.description}</td>
                          <td className="py-3 px-4 border-b border-slate-100 align-middle">
                            <span className="inline-block py-1 px-3 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{credit.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-between items-center py-3 px-4 mb-4 bg-slate-50 rounded-lg">
                  <button type="button" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="py-2 px-4 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
                  <span className="text-sm text-slate-600 font-medium">Page {currentPage} of {totalPages}</span>
                  <button type="button" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="py-2 px-4 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 py-4 px-5 border-t border-slate-200 bg-slate-50">
          <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-100" onClick={onClose}>Close</button>
          <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-blue-600 text-white border-none hover:bg-blue-700" onClick={() => window.location.href = '/manager/reports'}>View All Transactions →</button>
        </div>
      </div>
    </div>
  );
};

export default ManagerCreditModal;













